<template>
  <div class="min-h-screen bg-gray-50 overflow-x-hidden">
    <LoginScreen v-if="!isAuthenticated" @login-success="handleLoginSuccess" />

    <template v-else>
      <div class="flex flex-col h-screen bg-gray-100 overflow-x-hidden">
        <!-- New Unified Menu -->
        <MainMenu
          :user="currentUser"
          :language="currentLanguage"
          @navigate="handleMainMenuNavigate"
          @logout="handleLogout"
          @languageChange="handleLanguageChange"
          @toggleHistory="toggleHistoryDrawer"
        />


        <div class="flex flex-1 min-h-0 relative w-full overflow-x-hidden">
          <!-- HomePage - shown after login -->
          <HomePage
            v-if="currentView === 'home' && !showAdmin && !showPlantsSearch && !showSEC && !showTest && !showHeating && !showSchema && !showSimulator && !showNoiseCalculator && !showContactorCalculator && !showSheetMetalCalculator && !showInsulationCalculator && !showSECAlarmDecoder && !showHotjetRender && !showProjects && !showPdfTranslator && !showPumpMatch && !showPartsViewer && !showModelsAdmin && !showEprel && !shareTokenMode"
            class="w-full"
            @enterChat="handleEnterChat"
            @quickQuestion="handleQuickQuestion"
            @openBuildingManagement="handleOpenBuildingManagement"
          />

          <!-- Chat View with Sidebar -->
          <template v-else-if="currentView === 'chat' && !showAdmin && !showPlantsSearch && !showSEC && !showTest && !showSchema && !showSimulator && !showNoiseCalculator">
            <ChatSidebar
              :isOpen="chatSidebarOpen"
              :conversations="conversations"
              :activeConversationId="currentConversationId"
              @newChat="startNewChat"
              @selectConversation="loadConversation"
              @deleteConversation="deleteConversation"
              @close="chatSidebarOpen = false"
            />
            <main class="flex flex-col flex-1 h-full relative">
              <div class="flex-1 overflow-y-auto pb-40">
                <ChatWindow
                  :messages="messages"
                  :user="currentUser"
                  :shareTokenMode="shareTokenMode"
                  @suggestion="onSuggestion"
                  @retry="retryLastMessage"
                  @sendMessage="onSuggestion"
                  @editMessage="handleEditMessage"
                />
              </div>

              <!-- Chat Input -->
              <ChatInputArea
                v-if="!(shareTokenMode && shareTokenReadOnly)"
                :suggestions="CTA_SUGGESTIONS"
                :disabled="isWaitingForResponse"
                :isLoading="isWaitingForResponse"
                :responseLength="responseLength"
                :sessionId="sessionId"
                v-model="userInput"
                @send="handleSendMessage"
                @suggestion="onSuggestion"
                @setResponseLength="handleResponseLengthChange"
                @consultation="handleConsultation"
                @openRVSAnalyzer="handleOpenRVSAnalyzer"
                @cancel="cancelRequest"
              />
            </main>
          </template>

          <!-- Other Views (Admin, Analyzers, etc.) -->
          <main v-else class="flex flex-col flex-1 min-h-0 relative">
            <div class="flex-1 overflow-y-auto">
              <AdminPanel v-if="showAdmin" :user="currentUser" @close="closeAdmin" />
              <PlantsSearchView v-if="showPlantsSearch" :user="currentUser" :initialSearch="plantsSearchInitial" :freshdeskContext="freshdeskContext" @close="closePlantsSearch" />
              <MobileDashboard
                v-if="showSEC"
                :plant="{ name: 'RVS gen 2 test hotjet', deviceName: 'RVS gen 2 test hotjet' }"
                :user="currentUser"
                @close="closeSEC"
                @logout="handleLogout"
                @admin="openAdmin"
                @backToChat="backToChat"
              />
              <ClimatixTestDashboard
                v-if="showTest"
                :plant="{ name: 'RVS gen 2 test hotjet', deviceName: 'RVS gen 2 test hotjet' }"
                @close="closeTest"
              />
              <HeatingControlDashboard
                v-if="showHeating"
                @close="closeHeating"
              />
              <SchemaView v-if="showSchema" :user="currentUser" @close="closeSchema" />
              <HeatExchangerSimulator v-if="showSimulator" @close="closeSimulator" />
              <HeatPumpNoiseCalculator v-if="showNoiseCalculator" @close="closeNoiseCalculator" />
              <ContactorCalculator v-if="showContactorCalculator" @close="closeContactorCalculator" />
              <SheetMetalCalculator v-if="showSheetMetalCalculator" @close="closeSheetMetalCalculator" />
              <InsulationCalculator v-if="showInsulationCalculator" @close="closeInsulationCalculator" />
              <SECAlarmDecoder v-if="showSECAlarmDecoder" @close="closeSECAlarmDecoder" />
              <HotjetRenderGenerator v-if="showHotjetRender" @close="closeHotjetRender" />
              <ProjectsManager v-if="showProjects" :user="currentUser" :initialProjectType="initialProjectType" @close="closeProjects" />
              <PdfTranslator v-if="showPdfTranslator" @close="closePdfTranslator" />
              <PumpMatch v-if="showPumpMatch" @close="closePumpMatch" />
              <PartsViewer v-if="showPartsViewer" @close="showPartsViewer = false; currentView = 'home'" />
              <ModelsAdmin v-if="showModelsAdmin" @close="showModelsAdmin = false; currentView = 'home'" />
              <EprelSearch v-if="showEprel" @close="showEprel = false; currentView = 'home'" />
            </div>
          </main>
        </div>
      </div>
    </template>
  </div>

  <ExportModal
    :show="showExportModal"
    @select="handleFormatSelect"
    @close="showExportModal = false"
  />

  <RVSAnalyzerUpload
    :isOpen="showRVSAnalyzer"
    @close="showRVSAnalyzer = false"
    @analysis-complete="handleRVSAnalysisComplete"
  />

  <SECLogAnalyzer
    :isOpen="showSECAnalyzer"
    @close="showSECAnalyzer = false"
  />

  <SECCloudAnalyzer
    :isOpen="showSECCloudAnalyzer"
    @close="showSECCloudAnalyzer = false"
    @analysis-complete="handleSECCloudAnalysisComplete"
  />

  <FreshdeskAnalyzer
    :isOpen="showFreshdeskAnalyzer"
    @close="showFreshdeskAnalyzer = false"
    @analysis-complete="handleFreshdeskAnalysisComplete"
    @openPlantsSearch="handleOpenPlantsSearchFromFreshdesk"
  />

  <ServiceTechnicians
    :isOpen="showServiceTechnicians"
    @close="showServiceTechnicians = false"
  />

  <ToastNotification
    :notification="notification"
    @close="hideNotification"
  />
</template>

<script setup>
import {
  ref,
  computed,
  onMounted,
  onUnmounted,
  nextTick,
  watch,
  onErrorCaptured,
  triggerRef,
  defineAsyncComponent,
} from "vue";

// Eager: shell (menu, home, chat, login, toast) + modaly trvale v DOM (řízené :isOpen/:show).
import ChatWindow from "./components/ChatWindow.vue";
import ChatInputArea from "./components/ChatInputArea.vue";
import ChatSidebar from "./components/ChatSidebar.vue";
import HistoryDrawer from "./components/HistoryDrawer.vue";
import LoginScreen from "./components/LoginScreen.vue";
import MainMenu from "./components/MainMenu.vue";
import HomePage from "./components/HomePage.vue";
import ToastNotification from "./components/ToastNotification.vue";
import ExportModal from "./components/ExportModal.vue";
import RVSAnalyzerUpload from "./components/RVSAnalyzerUpload.vue";
import SECLogAnalyzer from "./components/SECLogAnalyzer.vue";
import SECCloudAnalyzer from "./components/SECCloudAnalyzer.vue";
import FreshdeskAnalyzer from "./components/FreshdeskAnalyzer.vue";
import ServiceTechnicians from "./components/ServiceTechnicians.vue";

// Lazy: views otevírané přes v-if="show*" — kód se stáhne až při prvním zobrazení.
const AdminPanel = defineAsyncComponent(() => import("./components/AdminPanel.vue"));
const PlantsSearchView = defineAsyncComponent(() => import("./components/PlantsSearchView.vue"));
const MobileDashboard = defineAsyncComponent(() => import("./components/MobileDashboard.vue"));
const ClimatixTestDashboard = defineAsyncComponent(() => import("./components/ClimatixTestDashboard.vue"));
const HeatingControlDashboard = defineAsyncComponent(() => import("./components/HeatingControlDashboard.vue"));
const SchemaView = defineAsyncComponent(() => import("./components/SchemaView.vue"));
const HeatExchangerSimulator = defineAsyncComponent(() => import("./components/HeatExchangerSimulator.vue"));
const HeatPumpNoiseCalculator = defineAsyncComponent(() => import("./components/HeatPumpNoiseCalculator.vue"));
const ContactorCalculator = defineAsyncComponent(() => import("./components/ContactorCalculator.vue"));
const SheetMetalCalculator = defineAsyncComponent(() => import("./components/SheetMetalCalculator.vue"));
const InsulationCalculator = defineAsyncComponent(() => import("./components/InsulationCalculator.vue"));
const SECAlarmDecoder = defineAsyncComponent(() => import("./components/SECAlarmDecoder.vue"));
const HotjetRenderGenerator = defineAsyncComponent(() => import("./components/HotjetRenderGenerator.vue"));
const ProjectsManager = defineAsyncComponent(() => import("./components/projects/ProjectsManager.vue"));
const PdfTranslator = defineAsyncComponent(() => import("./components/PdfTranslator.vue"));
const PumpMatch = defineAsyncComponent(() => import("./components/PumpMatch.vue"));
const PartsViewer = defineAsyncComponent(() => import("./components/PartsViewer.vue"));
const ModelsAdmin = defineAsyncComponent(() => import("./components/ModelsAdmin.vue"));
const EprelSearch = defineAsyncComponent(() => import("./components/eprel/EprelSearch.vue"));

// Composables
import { useNotifications } from "./composables/useNotifications";
import { usePermissions } from "./composables/usePermissions";
import { useApi } from "./composables/useApi.js";

// ===========================================
// Composables Setup
// ===========================================

// Notifications (used from composable)
const { notification, showNotification, hideNotification } = useNotifications();

// Permissions (used for role-based access control)
const { user: permissionUser, hasModule, isAdmin, isSuperAdmin, loadUser } = usePermissions();

// API composable
const { get: apiGet, post: apiPost, put: apiPut, del: apiDel } = useApi();

// ===========================================
// Error Handling
// ===========================================

// Global error handler pro uncaught promises
window.addEventListener("unhandledrejection", (event) => {
  if (
    event.reason?.message?.includes("message channel closed") ||
    event.reason?.message?.includes("listener indicated")
  ) {
    console.warn("Browser extension error (ignored):", event.reason.message);
    event.preventDefault();
    return;
  }
  console.error("Unhandled promise rejection:", event.reason);
  showNotification("Došlo k neočekávané chybě", "error");
});

onErrorCaptured((error, instance, info) => {
  console.error("Component error:", error, info);
  showNotification("Chyba komponenty", "error");
  return false;
});

// ===========================================
// Reactive State
// ===========================================

// UI constants
const CTA_SUGGESTIONS = [
  // Skryto do odvolání:
  // "Konzultaci s technikem",
  // "Nezávaznou nabídku",
  // "Vyřízení dotací",
];

// Auth state
const currentUser = ref(null);
const authToken = ref(null);
const shareTokenMode = ref(false);
const shareTokenData = ref(null);
const shareTokenReadOnly = ref(false); // NOVÉ: Read-only režim pro share token
const shareTokenResponseGenerated = ref(false); // NOVÉ: Byla už vygenerována odpověď?

// 🔧 DEV MODE: Bypass authentication for local development
const DEV_BYPASS_AUTH = import.meta.env.DEV && import.meta.env.VITE_BYPASS_AUTH === 'true';

const isAuthenticated = computed(() =>
  (DEV_BYPASS_AUTH && currentUser.value) || (currentUser.value && authToken.value) || shareTokenMode.value
);

// UI state
const showAdmin = ref(false);
const showPlantsSearch = ref(false);
const plantsSearchInitial = ref('');
const freshdeskContext = ref(null); // Context from Freshdesk for plant linking
const showSEC = ref(false);
const showTest = ref(false);
const showHeating = ref(false);
const showSchema = ref(false);
const showSimulator = ref(false);
const showNoiseCalculator = ref(false);
const showContactorCalculator = ref(false);
const showSheetMetalCalculator = ref(false);
const showInsulationCalculator = ref(false);
const showSECAlarmDecoder = ref(false);
const showHotjetRender = ref(false);
const showProjects = ref(false);
const showPdfTranslator = ref(false);
const showPumpMatch = ref(false);
const showPartsViewer = ref(false);
const showModelsAdmin = ref(false);
const showEprel = ref(false);
const initialProjectType = ref(null); // 'building', 'misc', or null

// New navigation state
const currentView = ref('home'); // 'home' | 'chat' | other views
const showHistoryDrawer = ref(false);
const chatSidebarOpen = ref(false); // Closed by default, especially on mobile

const isWaitingForResponse = ref(false);
const currentLanguage = ref("cs");
const userInput = ref("");
const messages = ref([]);
const isMobile = ref(false);
const responseLength = ref("normal");
const apiResponseLength = ref("normal");
const sessionId = ref(null);
const showExportModal = ref(false);
const showRVSAnalyzer = ref(false);
const showSECAnalyzer = ref(false);
const showSECCloudAnalyzer = ref(false);
const showFreshdeskAnalyzer = ref(false);
const showServiceTechnicians = ref(false);

// Conversation history
const conversations = ref([]);
const currentConversationId = ref(null);
const isLoadingConversations = ref(false);

// Request management
const abortController = ref(null);
const lastUserMessage = ref("");
const retryCount = ref(0);
const maxRetries = 3;

// Performance tracking
const aiModel = ref(null); // Aktuální AI model z databáze

const performanceMetrics = ref({
  messagesSent: 0,
  totalResponseTime: 0,
  averageResponseTime: 0,
  errors: 0,
  retries: 0,
});

// Computed properties
const conversationHistory = computed(() => {
  return messages.value
    .filter((m) => !m.isError && !m.isLoading)
    .map((m) => ({
      role: m.sender === "user" ? "user" : "assistant",
      content: m.text || m.content || "",
    }));
});

// ===========================================
// Auth Functions
// ===========================================

const checkAuthStatus = async () => {
  const storedAuth = localStorage.getItem("userAuth");
  if (storedAuth) {
    try {
      const authData = JSON.parse(storedAuth);
      if (authData.expiresAt && new Date(authData.expiresAt) > new Date()) {
        authToken.value = authData.token;

        // Check if user already has role/permissions (from previous load)
        if (authData.user?.role) {
          currentUser.value = authData.user;
          console.log('✅ Restored user with role:', authData.user.email, 'role:', authData.user.role);
        } else {
          // Fetch fresh user data with role/permissions from database
          try {
            const response = await fetch('/api/users/me', {
              headers: {
                'Authorization': `Bearer ${authData.token}`
              }
            });

            if (response.ok) {
              const data = await response.json();
              if (data.success && data.user) {
                currentUser.value = {
                  ...authData.user,
                  ...data.user,
                  picture: authData.user?.picture || data.user.avatar_url || '/img/user-avatar.svg'
                };
                console.log('✅ User refreshed with permissions:', data.user.email, 'role:', data.user.role);

                // Update localStorage with full user data
                localStorage.setItem('userAuth', JSON.stringify({
                  ...authData,
                  user: currentUser.value
                }));
              } else {
                currentUser.value = authData.user;
              }
            } else {
              currentUser.value = authData.user;
            }
          } catch (e) {
            console.warn('Could not refresh user permissions:', e);
            currentUser.value = authData.user;
          }
        }
      } else {
        localStorage.removeItem("userAuth");
        currentUser.value = null;
        authToken.value = null;
      }
    } catch (error) {
      console.error("Error parsing auth data:", error);
      localStorage.removeItem("userAuth");
    }
  }
};

const handleLoginSuccess = async (user) => {
  const authData = JSON.parse(localStorage.getItem("userAuth"));
  authToken.value = authData.token;

  // Clear dev logout flag - allow auto-login again
  localStorage.removeItem('devLoggedOut');

  // Fetch full user data with role and permissions from database
  try {
    const response = await fetch('/api/users/me', {
      headers: {
        'Authorization': `Bearer ${authData.token}`
      }
    });

    if (response.ok) {
      const data = await response.json();
      if (data.success && data.user) {
        // Merge Google user data with database role/permissions
        currentUser.value = {
          ...user,
          ...data.user,
          picture: user.picture || data.user.avatar_url || '/img/user-avatar.svg'
        };
        console.log('✅ User loaded with permissions:', data.user.email, 'role:', data.user.role);

        // Update localStorage with full user data
        localStorage.setItem('userAuth', JSON.stringify({
          user: currentUser.value,
          token: authData.token,
          expiresAt: authData.expiresAt
        }));
      } else {
        // Fallback to Google user if API doesn't return user
        currentUser.value = user;
        console.warn('⚠️ Could not load user permissions from DB');
      }
    } else {
      // Fallback to Google user if API fails
      currentUser.value = user;
      console.warn('⚠️ Failed to fetch user from /api/users/me');
    }
  } catch (error) {
    console.error('Error loading user permissions:', error);
    // Fallback to Google user
    currentUser.value = user;
  }

  // Vyčistit stav předchozího uživatele
  messages.value = [];
  conversations.value = [];
  currentConversationId.value = null;
  sessionId.value = null;
  localStorage.removeItem("chatSessionId");

  // Načíst konverzace nového uživatele
  await loadConversations();

  showNotification("Úspěšně přihlášen", "success");
};

const handleLogout = () => {
  currentUser.value = null;
  authToken.value = null;
  localStorage.removeItem("userAuth");
  localStorage.removeItem("chatSessionId");

  // Flag pro DEV mode - zabrání auto-login po logout
  if (DEV_BYPASS_AUTH) {
    localStorage.setItem('devLoggedOut', 'true');
  }

  // Vyčistit veškerý stav aplikace
  messages.value = [];
  conversations.value = [];
  currentConversationId.value = null;
  sessionId.value = null;
  retryCount.value = 0;
  lastUserMessage.value = "";

  showNotification("Odhlášen", "info");
};

// ===========================================
// Share Token Functions
// ===========================================

const handleShareToken = async () => {
  const pathname = window.location.pathname;
  const shareTokenMatch = pathname.match(/^\/share\/([^\/]+)/);

  if (!shareTokenMatch) {
    return false;
  }

  const token = shareTokenMatch[1];
  console.log("🔗 Detekován share token:", token);

  try {
    // Validovat token přes API
    const response = await fetch("/api/share-tokens/validate", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token }),
    });

    const data = await response.json();

    if (!data.success) {
      showNotification(
        `❌ Neplatný odkaz: ${data.error}`,
        "error"
      );
      // Redirect na homepage po 3 sekundách
      setTimeout(() => {
        window.location.href = "/";
      }, 3000);
      return false;
    }

    // Aktivovat share token mode
    shareTokenMode.value = true;
    shareTokenData.value = data;
    shareTokenReadOnly.value = data.readOnly !== false; // Default TRUE

    // Předvyplnit input
    userInput.value = data.prompt;

    // Nastavit response style
    if (data.responseStyle) {
      responseLength.value = data.responseStyle;
      apiResponseLength.value = data.responseStyle;
    }

    // Vytvoř guest uživatele pro zobrazení
    currentUser.value = {
      email: "guest",
      name: "Guest User",
      picture: null,
    };

    // Různé notifikace pro read-only vs interaktivní režim
    if (shareTokenReadOnly.value) {
      showNotification(
        "📄 Načítám obsah...",
        "info"
      );
    } else {
      showNotification(
        "✅ Odkaz načten - můžete upravit dotaz nebo rovnou odeslat",
        "success"
      );
    }

    // Auto-send pokud je nastaveno
    if (data.autoSend) {
      await nextTick();
      setTimeout(() => {
        handleSendMessage();
      }, 1000);
    }

    return true;
  } catch (error) {
    console.error("Chyba při validaci share tokenu:", error);
    showNotification("❌ Chyba při načítání odkazu", "error");
    return false;
  }
};

async function openAdmin() {
  if (hasModule('admin')) {
    showAdmin.value = true;
    showPlantsSearch.value = false;
    showSchema.value = false;
  } else {
    alert("Přístup pouze pro administrátory");
  }
}

function openPlantsSearch() {
  if (hasModule('cloud')) {
    showPlantsSearch.value = true;
    showAdmin.value = false;
    showSchema.value = false;
  } else {
    alert("Nemáte přístup k modulu Cloud");
  }
}

function openSchema() {
  if (hasModule('cloud')) {
    showSchema.value = true;
    showAdmin.value = false;
    showPlantsSearch.value = false;
  } else {
    alert("Nemáte přístup k modulu Cloud");
  }
}

function closeAdmin() {
  showAdmin.value = false;
  currentView.value = 'home';
}

function closePlantsSearch() {
  showPlantsSearch.value = false;
  plantsSearchInitial.value = '';
  freshdeskContext.value = null;
  currentView.value = 'home';
}

function openSEC() {
  if (hasModule('analyzy')) {
    showSEC.value = true;
    showAdmin.value = false;
    showPlantsSearch.value = false;
    showSchema.value = false;
  } else {
    alert("Nemáte přístup k modulu Analýzy");
  }
}

function closeSEC() {
  showSEC.value = false;
  currentView.value = 'home';
}

function openTest() {
  if (hasModule('cloud')) {
    showTest.value = true;
    showAdmin.value = false;
    showPlantsSearch.value = false;
    showSEC.value = false;
    showSchema.value = false;
  } else {
    alert("Nemáte přístup k modulu Cloud");
  }
}

function closeTest() {
  showTest.value = false;
  currentView.value = 'home';
}

function openHeating() {
  if (hasModule('cloud')) {
    showHeating.value = true;
    showAdmin.value = false;
    showPlantsSearch.value = false;
    showSEC.value = false;
    showTest.value = false;
    showSchema.value = false;
  } else {
    alert("Nemáte přístup k modulu Cloud");
  }
}

function closeHeating() {
  showHeating.value = false;
  currentView.value = 'home';
}

function closeSchema() {
  showSchema.value = false;
  currentView.value = 'home';
}

function openHeatExchangerSimulator() {
  showSimulator.value = true;
  showNoiseCalculator.value = false;
  showAdmin.value = false;
  showPlantsSearch.value = false;
  showSEC.value = false;
  showSchema.value = false;
}

function closeSimulator() {
  showSimulator.value = false;
  currentView.value = 'home';
}

function openNoiseCalculator() {
  showNoiseCalculator.value = true;
  showSimulator.value = false;
  showAdmin.value = false;
  showPlantsSearch.value = false;
  showSEC.value = false;
  showSchema.value = false;
}

function closeNoiseCalculator() {
  showNoiseCalculator.value = false;
  currentView.value = 'home';
}

function closeContactorCalculator() {
  showContactorCalculator.value = false;
  currentView.value = 'home';
}

function closeSheetMetalCalculator() {
  showSheetMetalCalculator.value = false;
  currentView.value = 'home';
}

function closeInsulationCalculator() {
  showInsulationCalculator.value = false;
  currentView.value = 'home';
}

function closeSECAlarmDecoder() {
  showSECAlarmDecoder.value = false;
  currentView.value = 'home';
}

function closeHotjetRender() {
  showHotjetRender.value = false;
  currentView.value = 'home';
}

function closeProjects() {
  showProjects.value = false;
  initialProjectType.value = null;
  currentView.value = 'home';
}

function closePdfTranslator() {
  showPdfTranslator.value = false;
  currentView.value = 'home';
}

function closePumpMatch() {
  showPumpMatch.value = false;
  currentView.value = 'home';
}

function backToChat() {
  showAdmin.value = false;
  showPlantsSearch.value = false;
  showSEC.value = false;
  showTest.value = false;
  showHeating.value = false;
  showSchema.value = false;
  showSimulator.value = false;
  showNoiseCalculator.value = false;
  showContactorCalculator.value = false;
  showSheetMetalCalculator.value = false;
  showInsulationCalculator.value = false;
  showSECAlarmDecoder.value = false;
  showProjects.value = false;
  showPdfTranslator.value = false;
  showPumpMatch.value = false;
  showPartsViewer.value = false;
  currentView.value = 'chat';
}

// Navigation handler for MainMenu
function handleMainMenuNavigate(view) {
  // Close all views first
  showAdmin.value = false;
  showPlantsSearch.value = false;
  showSEC.value = false;
  showTest.value = false;
  showHeating.value = false;
  showSchema.value = false;
  showSimulator.value = false;
  showNoiseCalculator.value = false;
  showContactorCalculator.value = false;
  showSheetMetalCalculator.value = false;
  showInsulationCalculator.value = false;
  showSECAlarmDecoder.value = false;
  showProjects.value = false;
  showPdfTranslator.value = false;
  showPumpMatch.value = false;
  showPartsViewer.value = false;
  showModelsAdmin.value = false;
  showEprel.value = false;
  showRVSAnalyzer.value = false;
  showSECAnalyzer.value = false;
  showFreshdeskAnalyzer.value = false;
  showServiceTechnicians.value = false;
  showHistoryDrawer.value = false;

  // Set new view
  switch (view) {
    case 'home':
      currentView.value = 'home';
      break;
    case 'chat':
      // Start new chat when clicking "Nový chat"
      startNewChat();
      currentView.value = 'chat';
      break;
    case 'admin':
      showAdmin.value = true;
      currentView.value = 'other';
      break;
    case 'ozw':
      showPlantsSearch.value = true;
      currentView.value = 'other';
      break;
    case 'climatix':
      showSEC.value = true;
      currentView.value = 'other';
      break;
    case 'test':
      showTest.value = true;
      currentView.value = 'other';
      break;
    case 'heating':
      showHeating.value = true;
      currentView.value = 'other';
      break;
    case 'rvsAnalyzer':
      showRVSAnalyzer.value = true;
      // Modal overlay - don't change currentView, keep home/chat visible behind
      if (currentView.value === 'other') currentView.value = 'home';
      break;
    case 'secAnalyzer':
      showSECAnalyzer.value = true;
      // Modal overlay - don't change currentView
      if (currentView.value === 'other') currentView.value = 'home';
      break;
    case 'secCloudAnalyzer':
      showSECCloudAnalyzer.value = true;
      // Modal overlay - don't change currentView
      if (currentView.value === 'other') currentView.value = 'home';
      break;
    case 'freshdeskAnalyzer':
      showFreshdeskAnalyzer.value = true;
      // Modal overlay - don't change currentView
      if (currentView.value === 'other') currentView.value = 'home';
      break;
    case 'serviceTechnicians':
      showServiceTechnicians.value = true;
      // Modal overlay - don't change currentView
      if (currentView.value === 'other') currentView.value = 'home';
      break;
    case 'heatExchanger':
      showSimulator.value = true;
      currentView.value = 'other';
      break;
    case 'noiseCalculator':
      showNoiseCalculator.value = true;
      currentView.value = 'other';
      break;
    case 'contactorCalculator':
      showContactorCalculator.value = true;
      currentView.value = 'other';
      break;
    case 'sheetMetalCalculator':
      showSheetMetalCalculator.value = true;
      currentView.value = 'other';
      break;
    case 'insulationCalculator':
      showInsulationCalculator.value = true;
      currentView.value = 'other';
      break;
    case 'secAlarmDecoder':
      showSECAlarmDecoder.value = true;
      currentView.value = 'other';
      break;
    case 'hotjetRender':
      showHotjetRender.value = true;
      currentView.value = 'other';
      break;
    case 'projects':
      showProjects.value = true;
      currentView.value = 'other';
      break;
    case 'pdfTranslator':
      showPdfTranslator.value = true;
      currentView.value = 'other';
      break;
    case 'pumpMatch':
      showPumpMatch.value = true;
      currentView.value = 'other';
      break;
    case 'partsViewer':
      showPartsViewer.value = true;
      currentView.value = 'other';
      break;
    case 'modelsAdmin':
      showModelsAdmin.value = true;
      currentView.value = 'other';
      break;
    case 'eprel':
      showEprel.value = true;
      currentView.value = 'other';
      break;
    default:
      currentView.value = 'home';
  }
}

function toggleHistoryDrawer() {
  // If in chat view, toggle the persistent sidebar
  if (currentView.value === 'chat') {
    chatSidebarOpen.value = !chatSidebarOpen.value;
  } else {
    // If not in chat, switch to chat view and ensure sidebar is open
    currentView.value = 'chat';
    chatSidebarOpen.value = true;
  }
}

function handleEnterChat() {
  currentView.value = 'chat';
}

function handleOpenBuildingManagement() {
  // Open projects view directly with building project
  initialProjectType.value = 'building';
  showProjects.value = true;
  currentView.value = 'other';
}

function handleQuickQuestion(text) {
  currentView.value = 'chat';
  // Slight delay to ensure chat view is rendered
  setTimeout(() => {
    onSuggestion(text);
  }, 100);
}

// ===========================================
// Request Management
// ===========================================

function cancelRequest() {
  if (abortController.value) {
    abortController.value.abort();
    showNotification("Požadavek byl zrušen", "info");
  }
}

async function retryLastMessage() {
  if (lastUserMessage.value && retryCount.value < maxRetries) {
    retryCount.value++;
    performanceMetrics.value.retries++;

    // Odstranit poslední error zprávu
    messages.value = messages.value.filter((m) => !m.isError);

    showNotification(
      `Opakuji požadavek (${retryCount.value}/${maxRetries})`,
      "info"
    );
    await handleSendMessage(lastUserMessage.value, true);
  } else {
    showNotification("Maximální počet opakování dosažen", "error");
  }
}

// ===========================================
// Message Handling - OPRAVENÁ VERZE PRO STREAMING
// ===========================================

const handleSendMessage = async (message = null, attachments = [], isRetry = false) => {
  // Use provided message or current input
  let messageText = message || userInput.value?.trim();

  // Povolit odeslání i bez textu pokud jsou přílohy
  if (!messageText && (!attachments || attachments.length === 0)) {
    showNotification("Napište zprávu nebo přiložte soubor", "error");
    return;
  }

  // Detekce Manual Mode pro generování dokumentace
  let useManualMode = false;
  const manualModeRegex = /\bmanual\s+mode\b/i;
  if (manualModeRegex.test(messageText)) {
    useManualMode = true;
    messageText = messageText.replace(manualModeRegex, '').trim();
    console.log("🔧 Manual Mode aktivován pro tuto zprávu");
    showNotification("📝 Manual Mode aktivován - generuji dokumentaci", "info");
  }

  // 🔥 Detekce /oprava command pro ukládání oprav do znalostní báze
  const correctionRegex = /^\/oprava\s+(.+)$/i;
  const correctionMatch = messageText.match(correctionRegex);
  if (correctionMatch) {
    const correctionText = correctionMatch[1].trim();
    console.log("✏️ Correction command detected:", correctionText);

    // 📝 Zachytit kontext - poslední AI odpověď (chyba) a původní dotaz
    const lastAssistantMessage = [...messages.value]
      .reverse()
      .find(m => m.sender === 'assistant' || m.role === 'assistant');

    const lastUserMessage = [...messages.value]
      .reverse()
      .find(m => m.sender === 'user' || m.role === 'user');

    const originalContext = lastAssistantMessage?.text || lastAssistantMessage?.content || null;
    const userQuery = lastUserMessage?.text || lastUserMessage?.content || null;

    console.log("📝 Context captured:", {
      userQuery,
      originalContext: originalContext?.substring(0, 100)
    });

    try {
      const data = await apiPost('/api/knowledge/add-correction', {
        text: correctionText,
        correctionType: 'clarification',
        category: 'Dokumentace',
        originalContext,
        userQuery
      });

      if (data.success) {
        showNotification(`✅ ${data.message}`, "success");
        // Přidat potvrzení do chatu
        const confirmMessage = {
          id: Date.now(),
          content: `✅ Oprava byla uložena do znalostní báze.\n\n**Text:** ${correctionText}\n\n${data.approved ? '✓ Automaticky schváleno' : '⏳ Čeká na schválení adminem'}`,
          role: "assistant",
          timestamp: new Date(),
          text: `✅ Oprava byla uložena do znalostní báze.\n\n**Text:** ${correctionText}\n\n${data.approved ? '✓ Automaticky schváleno' : '⏳ Čeká na schválení adminem'}`,
          sender: "assistant",
        };
        messages.value.push(confirmMessage);
      } else {
        showNotification("❌ Nepodařilo se uložit opravu", "error");
      }
    } catch (error) {
      console.error("Error saving correction:", error);
      showNotification("❌ Chyba při ukládání opravy", "error");
    }

    return; // Neposílat zprávu do AI
  }

  // Duplicate protection
  if (isWaitingForResponse.value) {
    showNotification(
      "Prosím počkejte na dokončení předchozího požadavku",
      "error"
    );
    return;
  }

  // Abort any existing request
  if (abortController.value) {
    abortController.value.abort();
  }

  // Create new controller
  abortController.value = new AbortController();

  // Performance tracking
  const startTime = Date.now();
  let messageIndex;

  try {
    isWaitingForResponse.value = true;

    if (!isRetry) {
      lastUserMessage.value = messageText;
      retryCount.value = 0;
    }

    // Add user message (only if not retry)
    if (!isRetry) {
      console.log(`📤 Before adding message: ${messages.value.length} messages, conversationId: ${currentConversationId.value}`);

      // Create conversation if first message
      if (!currentConversationId.value && authToken.value) {
        await createConversation(messageText);
      }

      const userMessage = {
        id: Date.now(),
        content: messageText,
        role: "user",
        timestamp: new Date(),
        text: messageText,
        sender: "user",
        attachments: attachments.length > 0 ? attachments : undefined,
      };
      messages.value.push(userMessage);
      console.log(`📤 After adding message: ${messages.value.length} messages`);
      userInput.value = "";

      // Save user message to DB
      await saveMessageToConversation('user', messageText, useManualMode ? 'manual' : responseLength.value);
    }

    // Remove any existing loading messages
    messages.value = messages.value.filter((m) => !m.isLoading);

    // Add new loading message
    const loadingMessage = {
      id: Date.now() + 1,
      content: "",
      role: "assistant",
      timestamp: new Date(),
      isStreaming: true,
      isLoading: true,
      text: "",
      sender: "assistant",
    };
    messages.value.push(loadingMessage);
    messageIndex = messages.value.length - 1;

    // Make the API request with session ID and attachments
    const response = await fetch("/api/stream", {
      signal: abortController.value.signal,
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${authToken.value}`,
      },
      body: JSON.stringify({
        input: messageText || '',
        responseLength: useManualMode ? "manual" : apiResponseLength.value,
        provider: "claude",
        sessionId: sessionId.value,
        attachments: attachments.length > 0 ? attachments : undefined,
      }),
    });

    if (!response.ok) {
      throw new Error(`API Error: ${response.status}`);
    }

    // OPRAVENÁ STREAMING LOGIKA
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    let streamClosed = false;

    // Timeout a heartbeat monitoring
    let lastActivityTime = Date.now();

    const timeoutId = setTimeout(() => {
      if (!streamClosed && abortController.value) {
        console.log("Request timeout after 60 seconds");
        abortController.value.abort();
      }
    }, 60000);

    const heartbeatInterval = setInterval(() => {
      const timeSinceLastActivity = Date.now() - lastActivityTime;
      if (timeSinceLastActivity > 45000 && !streamClosed) {
        console.log("No activity for 45 seconds, aborting");
        abortController.value.abort();
      }
    }, 5000);

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        lastActivityTime = Date.now();
        buffer += decoder.decode(value, { stream: true });

        // Zpracuj všechny kompletní SSE události v bufferu
        while (true) {
          const eventEnd = buffer.indexOf("\n\n");
          if (eventEnd === -1) break;

          const eventBlock = buffer.slice(0, eventEnd);
          buffer = buffer.slice(eventEnd + 2);

          // Zpracuj event block
          const lines = eventBlock.split("\n");
          let eventData = null;

          for (const line of lines) {
            if (line.startsWith("data: ")) {
              const dataStr = line.slice(6).trim();
              if (!dataStr || dataStr === "[DONE]") continue;

              try {
                if (
                  dataStr === "[object Object]" ||
                  dataStr.includes("[object Object]")
                ) {
                  console.warn("Invalid stream data detected:", dataStr);
                  continue;
                }
                eventData = JSON.parse(dataStr);
              } catch (parseError) {
                console.error("Parse error for SSE data:", dataStr, parseError);
                continue;
              }
            }
          }

          // OPRAVENÉ zpracování eventData
          if (
            eventData &&
            messageIndex !== undefined &&
            messages.value[messageIndex]
          ) {
            if (eventData.type === "text") {
              // KLÍČOVÁ OPRAVA: Použij pouze 'text' property a splice pro Vue reactivity
              const currentMessage = messages.value[messageIndex];
              const newText = (currentMessage.text || "") + eventData.content;

              // Vytvoř nový objekt zprávy
              const updatedMessage = {
                ...currentMessage,
                text: newText,
                content: newText, // Drž synchronizaci
                isLoading: false, // Už není loading
              };

              // DŮLEŽITÉ: Použij splice pro Vue reactivity!
              messages.value.splice(messageIndex, 1, updatedMessage);

              // Force Vue reactivity update
              triggerRef(messages);

              console.log("📝 Text chunk added:", eventData.content);
            } else if (eventData.type === "cache_info") {
              if (eventData.cacheStats) {
                const currentMessage = messages.value[messageIndex];
                const updatedMessage = {
                  ...currentMessage,
                  cacheStats: eventData.cacheStats,
                };
                messages.value.splice(messageIndex, 1, updatedMessage);
                triggerRef(messages);
              }
            } else if (eventData.type === "done") {
              const tokenInfo = {
                messageId: messages.value[messageIndex].id,
                cacheStats: eventData.cacheStats || null,
                sources: eventData.sources?.length || 0,
                products: eventData.products?.length || 0,
              };
              console.log("🔍 Message completed:", tokenInfo);

              const currentMessage = messages.value[messageIndex];
              const finalMessage = {
                ...currentMessage,
                isStreaming: false,
                isLoading: false,
                sources: eventData.sources || [],
                products: eventData.products || [], // NOVÉ: Produktová data
                cacheStats: eventData.cacheStats || null,
                tokens: eventData.cacheStats
                  ? {
                      input: eventData.cacheStats.inputTokens || 0,
                      output: eventData.cacheStats.outputTokens || 0,
                      total:
                        (eventData.cacheStats.inputTokens || 0) +
                        (eventData.cacheStats.outputTokens || 0),
                    }
                  : null,
              };

              messages.value.splice(messageIndex, 1, finalMessage);
              triggerRef(messages);

              // Save assistant message to DB
              await saveMessageToConversation('assistant', finalMessage.content, useManualMode ? 'manual' : responseLength.value);

              // Uložíme sessionId pokud je nové
              if (eventData.sessionId && !sessionId.value) {
                sessionId.value = eventData.sessionId;
                localStorage.setItem("chatSessionId", eventData.sessionId);
              }

              // NOVÉ: Označit že response byl vygenerován (pro read-only režim)
              if (shareTokenMode.value && shareTokenReadOnly.value) {
                shareTokenResponseGenerated.value = true;
              }
            } else if (eventData.type === "error") {
              throw new Error(eventData.message || "Stream error");
            }
          }
        }
      }
    } finally {
      clearTimeout(timeoutId);
      clearInterval(heartbeatInterval);
      streamClosed = true;
      reader.releaseLock();
    }

    // Success metrics
    performanceMetrics.value.messagesSent++;
    const responseTime = Date.now() - startTime;
    performanceMetrics.value.totalResponseTime += responseTime;
    performanceMetrics.value.averageResponseTime =
      performanceMetrics.value.totalResponseTime /
      performanceMetrics.value.messagesSent;

    console.log(`💡 Response completed in ${responseTime}ms`);
  } catch (error) {
    console.error("Error in handleSendMessage:", error);
    handleMessageError(error, messageIndex);
  } finally {
    isWaitingForResponse.value = false;
    abortController.value = null;
  }
};

function handleMessageError(error, messageIndex) {
  performanceMetrics.value.errors++;

  if (
    error.name === "AbortError" ||
    error.message?.includes("message channel closed") ||
    error.message?.includes("listener indicated")
  ) {
    console.log("Request byl přerušen nebo browser extension conflict");
    if (messageIndex !== undefined && messages.value[messageIndex]) {
      messages.value = messages.value.filter(
        (m) => m.id !== messages.value[messageIndex].id
      );
    }
    return;
  }

  // Show error in chat
  if (messageIndex !== undefined && messages.value[messageIndex]) {
    const currentMessage = messages.value[messageIndex];

    let errorText;
    if (
      error.message.includes("Failed to fetch") ||
      error.message.includes("NetworkError")
    ) {
      errorText =
        "🌐 Problém se sítí. Zkontrolujte připojení a zkuste to znovu.";
      showNotification("Problém se sítí", "error");
    } else if (error.message.includes("500")) {
      errorText = "⚙️ Dočasný problém na serveru. Zkuste to za chvilku znovu.";
      showNotification("Problém serveru", "error");
    } else {
      errorText = `❌ Došlo k chybě: ${error.message}. Zkuste to prosím znovu.`;
      showNotification("Neočekávaná chyba", "error");
    }

    const errorMessage = {
      ...currentMessage,
      isStreaming: false,
      isLoading: false,
      isError: true,
      content: errorText,
      text: errorText,
      canRetry: retryCount.value < maxRetries,
    };

    messages.value.splice(messageIndex, 1, errorMessage);
    triggerRef(messages);
  }
}

// ===========================================
// UI Event Handlers
// ===========================================

async function scrollToBottom(smooth = true) {
  await nextTick();
  const chatContainer = document.querySelector("main .overflow-y-auto");
  if (chatContainer) {
    setTimeout(() => {
      const extraPadding = 100;
      chatContainer.scrollTo({
        top: chatContainer.scrollHeight + extraPadding,
        behavior: smooth ? "smooth" : "auto",
      });
    }, 50);
  }
}

function checkMobile() {
  isMobile.value = window.innerWidth < 768;
}

function handleLanguageChange(lang) {
  currentLanguage.value = lang;
  showNotification("Lokalizace rozhraní zatím není implementována", "info");
}

async function onSuggestion(text) {
  console.log("onSuggestion called with:", text);
  if (!isWaitingForResponse.value) {
    userInput.value = text;
    await handleSendMessage();
  } else {
    showNotification("Počkejte na dokončení předchozího požadavku", "error");
  }
}

function handleResponseLengthChange(length) {
  responseLength.value = length;
  apiResponseLength.value = length;
  showNotification(`Délka odpovědi: ${length}`, "info");
}

// Handle message edit
async function handleEditMessage({ messageId, newText, isAssistant }) {
  console.log(`✏️ Editing message ${messageId} with new text:`, newText);

  // Najdi index zprávy
  const msgIndex = messages.value.findIndex(m => m.id === messageId);
  if (msgIndex === -1) {
    console.error("Message not found:", messageId);
    return;
  }

  const message = messages.value[msgIndex];

  // Updatuj zprávu v UI - použij Vue reaktivní způsob
  // Vytvoř nový objekt místo mutace (zajistí reaktivitu)
  messages.value[msgIndex] = {
    ...message,
    text: newText,
    content: newText
  };
  // DŮLEŽITÉ: sender/role se zachovají díky spread operátoru

  if (isAssistant) {
    // Editace AI odpovědi - ulož do DB
    if (currentConversationId.value && authToken.value) {
      try {
        await apiPut('/api/conversations/update-message', {
          messageId,
          newContent: newText
        });
        showNotification("Odpověď upravena a uložena", "success");
      } catch (error) {
        console.error("Error updating message:", error);
        showNotification("Chyba při ukládání úpravy", "error");
      }
    } else {
      showNotification("Odpověď upravena", "success");
    }
  } else {
    // Editace user zprávy - smaž následující a regeneruj
    messages.value = messages.value.slice(0, msgIndex);
    userInput.value = newText;
    await handleSendMessage();
  }
}

function handleConsultation() {
  const consultationText =
    "Mám zájem o konzultaci s technikem ohledně tepelného čerpadla.";
  userInput.value = consultationText;
  handleSendMessage();
}

function handleOpenRVSAnalyzer() {
  showRVSAnalyzer.value = true;
}

function handleOpenSECAnalyzer() {
  showSECAnalyzer.value = true;
}

function handleRVSAnalysisComplete(htmlReport) {
  console.log('RVS Analysis completed');
  showNotification("Analýza RVS logů dokončena! Report je připraven.", "success");
  // Můžete volitelně přidat report do konverzace jako zprávu
}

function handleOpenSECCloudAnalyzer() {
  showSECCloudAnalyzer.value = true;
}

function handleSECCloudAnalysisComplete(data) {
  console.log('SEC Cloud Analysis completed');
  showNotification("Analýza SEC Cloud logů dokončena!", "success");
}

function handleOpenFreshdeskAnalyzer() {
  showFreshdeskAnalyzer.value = true;
}

function handleFreshdeskAnalysisComplete(data) {
  console.log('Freshdesk Analysis completed');
}

function handleOpenPlantsSearchFromFreshdesk(context) {
  // Context is an object: { customerName, customerEmail, ticketId, ticketSubject }
  if (typeof context === 'string') {
    // Backwards compatibility
    plantsSearchInitial.value = context;
    freshdeskContext.value = null;
  } else {
    plantsSearchInitial.value = context?.customerName || '';
    freshdeskContext.value = context;
  }
  showPlantsSearch.value = true;
}

function handleAddSuggestion(text) {
  onSuggestion(text);
}

function startNewChat() {
  console.log("🔵 startNewChat called");

  if (isWaitingForResponse.value) {
    showNotification("Dokončete nejprve současný požadavek", "error");
    return;
  }

  messages.value = [];
  currentConversationId.value = null;
  retryCount.value = 0;
  lastUserMessage.value = "";

  // Vygeneruj nové unikátní sessionId (pro file upload a izolaci chatů)
  // Používáme crypto.randomUUID pro maximální unikátnost
  sessionId.value = `session_${Date.now()}_${crypto.randomUUID().substring(0, 8)}`;
  localStorage.setItem("chatSessionId", sessionId.value);
  console.log("🆕 Nové session ID pro nový chat:", sessionId.value);

  // Close history drawer on mobile
  if (isMobile.value) {
    showHistoryDrawer.value = false;
  }

  showNotification("Nový chat zahájen", "success");
  console.log("Nový chat zahájen, historie vymazána");
}

// Load all conversations
async function loadConversations() {
  if (!authToken.value) return;

  try {
    isLoadingConversations.value = true;
    const data = await apiGet('/api/conversations/list');
    conversations.value = data.conversations;
  } catch (error) {
    console.error('Error loading conversations:', error);
  } finally {
    isLoadingConversations.value = false;
  }
}

// Load specific conversation
async function loadConversation(conversationOrId) {
  if (!authToken.value) return;

  // Handle both object (from ChatSidebar) and string ID
  const conversationId = typeof conversationOrId === 'object' ? conversationOrId.id : conversationOrId;

  try {
    const data = await apiGet('/api/conversations/get', { id: conversationId });
    const conv = data.conversation;

    // Load messages into chat
    messages.value = conv.messages.map(msg => ({
      role: msg.role,
      sender: msg.role, // 'user' or 'assistant'
      text: msg.content,
      content: msg.content,
      timestamp: new Date(msg.created_at),
      responseStyle: msg.response_style
    }));

    currentConversationId.value = conversationId;
    currentView.value = 'chat'; // Switch to chat view when loading conversation
    console.log(`📥 Loaded ${messages.value.length} messages from conversation ${conversationId}`);
    showNotification("Konverzace načtena", "success");

    // Close history drawer on mobile
    if (isMobile.value) {
      showHistoryDrawer.value = false;
    }
  } catch (error) {
    console.error('Error loading conversation:', error);
    showNotification("Nepodařilo se načíst konverzaci", "error");
  }
}

// Delete conversation
async function deleteConversation(conversationOrId) {
  if (!authToken.value) return;

  // Handle both object and string ID
  const conversationId = typeof conversationOrId === 'object' ? conversationOrId.id : conversationOrId;

  if (!confirm('Opravdu chcete smazat tuto konverzaci?')) return;

  try {
    await apiDel(`/api/conversations/delete?id=${conversationId}`);
    // Remove from list
    conversations.value = conversations.value.filter(c => c.id !== conversationId);

    // If deleted current conversation, start new
    if (currentConversationId.value === conversationId) {
      startNewChat();
    }

    showNotification("Konverzace smazána", "success");
  } catch (error) {
    console.error('Error deleting conversation:', error);
    showNotification("Nepodařilo se smazat konverzaci", "error");
  }
}

// Save message to current conversation
async function saveMessageToConversation(role, content, responseStyle) {
  if (!authToken.value || !currentConversationId.value) return;

  try {
    await apiPost('/api/conversations/save-message', {
      conversationId: currentConversationId.value,
      role,
      content,
      responseStyle
    });

    // Aktualizovat updated_at v lokálním seznamu konverzací
    const convIndex = conversations.value.findIndex(c => c.id === currentConversationId.value);
    if (convIndex !== -1) {
      const conv = conversations.value[convIndex];
      conv.updated_at = new Date().toISOString();

      // Přesunout konverzaci na začátek seznamu
      conversations.value.splice(convIndex, 1);
      conversations.value.unshift(conv);
    }
  } catch (error) {
    console.error('Error saving message:', error);
  }
}

// Create new conversation from first message
async function createConversation(firstMessage) {
  if (!authToken.value) return null;

  try {
    // Generate title from first message (first 50 chars)
    const title = firstMessage.substring(0, 50) + (firstMessage.length > 50 ? '...' : '');

    const data = await apiPost('/api/conversations/create', { title });
    currentConversationId.value = data.conversation.id;

    // Add to conversations list
    conversations.value.unshift(data.conversation);

    return data.conversation.id;
  } catch (error) {
    console.error('Error creating conversation:', error);
  }

  return null;
}

// ===========================================
// Export Functions
// ===========================================

function stripHtml(html) {
  const tmp = document.createElement("DIV");
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || "";
}

function exportChatToEmail(format = "plain") {
  if (messages.value.length === 0) {
    showNotification("Není co exportovat - chat je prázdný", "error");
    return;
  }

  const chatDate = new Date().toLocaleDateString("cs-CZ");
  const messageCount = messages.value.length;
  const subject = `Köhler chat - ${chatDate} (${messageCount} zpráv)`;

  let chatText;
  if (format === "html") {
    chatText = messages.value
      .map((msg) => {
        const role = msg.sender === "user" ? "Vy" : "Köhler Inteligent";
        const timestamp = new Date(msg.timestamp).toLocaleString("cs-CZ");
        const text = msg.text || "";
        const backgroundColor = msg.sender === "user" ? "#dc2626" : "#ffffff";
        const textColor = msg.sender === "user" ? "#ffffff" : "#111827";

        return `
          <div style="margin: 1em 0; padding: 1em; border-radius: 1em; background-color: ${backgroundColor}; color: ${textColor}; border: 1px solid #e5e7eb;">
            <div style="font-size: 0.875em; color: #6b7280; margin-bottom: 0.5em;">[${timestamp}] ${role}:</div>
            <div style="line-height: 1.5;">${text}</div>
          </div>
        `;
      })
      .join("\n");

    chatText = `
      <div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;">
        <div style="margin-bottom: 2em;">
          <h2 style="font-size: 1.5em; margin-bottom: 0.5em;">Köhler Inteligent - Export chatu</h2>
          <div>Datum: ${chatDate}</div>
          <div>Počet zpráv: ${messageCount}</div>
          <div>Session ID: ${sessionId.value || "N/A"}</div>
          <div>Průměrná doba odezvy: ${Math.round(
            performanceMetrics.value.averageResponseTime
          )}ms</div>
        </div>
        <div style="margin: 2em 0;">
          ${chatText}
        </div>
        <div style="margin-top: 2em; padding-top: 1em; border-top: 1px solid #e5e7eb; color: #6b7280; font-size: 0.875em;">
          Vygenerováno z Köhler Inteligent chatbota
        </div>
      </div>
    `;
  } else {
    chatText = messages.value
      .map((msg) => {
        const role = msg.sender === "user" ? "Vy" : "Köhler Inteligent";
        const timestamp = new Date(msg.timestamp).toLocaleString("cs-CZ");
        const text = msg.text ? stripHtml(msg.text) : "";
        return `[${timestamp}] ${role}:\n${text}\n`;
      })
      .join("\n---\n\n");

    chatText =
      `Köhler Inteligent - Export chatu\n` +
      `Datum: ${chatDate}\n` +
      `Počet zpráv: ${messageCount}\n` +
      `Session ID: ${sessionId.value || "N/A"}\n` +
      `Statistiky: ${performanceMetrics.value.messagesSent} zpráv, ${performanceMetrics.value.errors} chyb, ${performanceMetrics.value.retries} opakování\n\n` +
      `=== KONVERZACE ===\n\n` +
      chatText +
      `\n\n---\nVygenerováno z Köhler Inteligent chatbota`;
  }

  const mailtoLink = `mailto:?subject=${encodeURIComponent(
    subject
  )}&body=${encodeURIComponent(chatText)}`;

  window.location.href = mailtoLink;
  showNotification("Export připraven", "success");
}

function handleExportClick() {
  showExportModal.value = true;
}

function handleFormatSelect(format) {
  showExportModal.value = false;
  exportChatToEmail(format);
}

async function resetCache() {
  if (
    !confirm(
      "Opravdu chcete vymazat všechnu historii a cache? Tato akce nelze vrátit."
    )
  ) {
    return;
  }

  try {
    const response = await fetch("/api/reset-cache", {
      method: "POST",
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    console.log("Cache reset response:", data);

    messages.value = [];
    sessionId.value = null;
    localStorage.removeItem("chatSessionId");

    // Reset performance metrics
    performanceMetrics.value = {
      messagesSent: 0,
      totalResponseTime: 0,
      averageResponseTime: 0,
      errors: 0,
      retries: 0,
    };

    showNotification("Cache a historie vymazány", "success");
    setTimeout(() => window.location.reload(), 1000);
  } catch (error) {
    console.error("Chyba při mazání cache:", error);
    showNotification(`Chyba při mazání cache: ${error.message}`, "error");
  }
}

// ===========================================
// Watchers & Lifecycle
// ===========================================

watch(
  messages,
  async (newMessages) => {
    await scrollToBottom();
  },
  {
    deep: true,
    flush: "post",
  }
);

// Load current AI model
async function loadAIModel() {
  try {
    if (!authToken.value) {
      aiModel.value = 'sonnet-5'; // fallback if not logged in
      return;
    }
    const data = await apiGet("/api/admin/ai-model");
    aiModel.value = data.model || 'sonnet-5';
  } catch (error) {
    console.error("Failed to load AI model:", error);
    aiModel.value = 'sonnet-5'; // fallback
  }
}

onMounted(async () => {
  // DEV MODE BYPASS - pouze localhost s VITE_BYPASS_AUTH=true
  const isDevBypass = DEV_BYPASS_AUTH && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

  // Zkontroluj, zda se uživatel explicitně neodhlásil
  const devLoggedOut = localStorage.getItem('devLoggedOut') === 'true';

  if (isDevBypass && !devLoggedOut) {
    authToken.value = 'dev-token-bypass';

    // Always fetch fresh user data from DB for dev mode
    try {
      const response = await fetch('/api/users/me', {
        headers: {
          'Authorization': 'Bearer dev-token-bypass'
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.user) {
          currentUser.value = {
            ...data.user,
            picture: data.user.avatar_url || '/img/user-avatar.svg'
          };

          // Save to localStorage for API calls
          localStorage.setItem('userAuth', JSON.stringify({
            user: currentUser.value,
            token: 'dev-token-bypass'
          }));

          console.log('🔧 DEV MODE: Loaded user from DB:', data.user.email, 'role:', data.user.role);
        }
      } else {
        console.warn('🔧 DEV MODE: Failed to load user from DB, using fallback');
        // Fallback to basic dev user
        const devUser = {
          id: 'dev-admin',
          email: 'kohler@hotjet.cz',
          name: 'Dev Admin',
          picture: '/img/user-avatar.svg',
          role: 'admin'
        };
        currentUser.value = devUser;
        localStorage.setItem('userAuth', JSON.stringify({
          user: devUser,
          token: 'dev-token-bypass'
        }));
      }
    } catch (e) {
      console.error('🔧 DEV MODE: Error loading user:', e);
      // Fallback
      const devUser = {
        id: 'dev-admin',
        email: 'kohler@hotjet.cz',
        name: 'Dev Admin',
        picture: '/img/user-avatar.svg',
        role: 'admin'
      };
      currentUser.value = devUser;
      localStorage.setItem('userAuth', JSON.stringify({
        user: devUser,
        token: 'dev-token-bypass'
      }));
    }
  } else if (devLoggedOut) {
    console.log('🔧 DEV MODE: User explicitly logged out, showing login screen');
  } else {
    // Nejdřív zkontroluj, jestli není share token v URL
    const hasShareToken = await handleShareToken();

    // Pokud není share token, použij normální autentizaci
    if (!hasShareToken) {
      await checkAuthStatus();
    }
  }

  checkMobile();
  window.addEventListener("resize", checkMobile);

  const storedSessionId = localStorage.getItem("chatSessionId");
  if (storedSessionId) {
    sessionId.value = storedSessionId;
    console.log("Načteno session ID:", storedSessionId);
  } else {
    // Vygeneruj nové session ID pro file upload
    sessionId.value = `session_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
    localStorage.setItem("chatSessionId", sessionId.value);
    console.log("Vytvořeno nové session ID:", sessionId.value);
  }

  // Load conversation history pouze pokud není share token mode
  if (!shareTokenMode.value) {
    await loadConversations();
  }

  // Load current AI model
  await loadAIModel();

  // Load user permissions
  if (currentUser.value && authToken.value) {
    await loadUser();
  }
});

onUnmounted(() => {
  window.removeEventListener("resize", checkMobile);

  // Cleanup ongoing requests
  if (abortController.value) {
    abortController.value.abort();
  }

  // Cleanup notification timeout
  if (notification.value.timeout) {
    clearTimeout(notification.value.timeout);
  }
});

// ===========================================
// Expose Functions
// ===========================================

defineExpose({
  startNewChat,
  exportChatToEmail,
  resetCache,
  handleLogout,
  cancelRequest,
  retryLastMessage,
  showNotification,
  performanceMetrics: performanceMetrics.value,
});
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.slide-enter-active,
.slide-leave-active {
  transition: transform 0.3s ease;
}

.slide-enter-from,
.slide-leave-to {
  transform: translateX(-100%);
}

.toast-enter-active,
.toast-leave-active {
  transition: all 0.3s ease;
}

.toast-enter-from {
  opacity: 0;
  transform: translateX(100%);
}

.toast-leave-to {
  opacity: 0;
  transform: translateX(100%);
}

/* Loading indicator pro input */
.loading-input {
  position: relative;
}

.loading-input::after {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 2px;
  background: linear-gradient(90deg, transparent, #3b82f6, transparent);
  animation: loading 1.5s infinite;
}

@keyframes loading {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
}

/* Smooth animations */
* {
  scroll-behavior: smooth;
}

/* Mobile optimizations */
@media (max-width: 768px) {
  .toast-enter-from,
  .toast-leave-to {
    transform: translateY(-100%);
  }
}
</style>
